import {reportBug} from '@packages/utilities/reporter';
import {CELL_DIRECTIONS} from './constants';
import type {WebflowCssProperties} from '@packages/systems/core/utils/WebflowCssSubset';
import {splitCssValue} from '@packages/systems/core/utils/CssStringUtils';
import type {
  Cell,
  CellId,
  ColumnCoord,
  RowCoord,
  CellCoordinates,
  CellLocation,
  CellSpans,
  LayoutMatrix,
  LayoutLocations,
  CellDirection,
} from './types';

/**
 * Fills matrix with given value at given CellLocation
 */
export function fillLayoutMatrixByLocation(
  matrix: LayoutMatrix,
  location: CellLocation,
  value: string
): LayoutMatrix {
  const {gridRowStart, gridRowEnd, gridColumnStart, gridColumnEnd} = location;

  const rowStart = gridRowStart - 1;
  const rowEnd = gridRowEnd - 1;
  const colStart = gridColumnStart - 1;
  const colEnd = gridColumnEnd - 1;

  for (let row = rowStart; row < rowEnd; row++) {
    for (let col = colStart; col < colEnd; col++) {
      if (!matrix[row]) {
        matrix[row] = [];
      }
      // @ts-expect-error - TS2532 - Object is possibly 'undefined'.
      matrix[row][col] = value;
    }
  }

  return matrix;
}

/**
 * Fills matrix with given value at given CellCoordinates
 */
export function fillLayoutMatrixByCoordinates(
  matrix: LayoutMatrix,
  coordinates: CellCoordinates,
  value: string
): LayoutMatrix {
  for (const [row, col] of coordinates) {
    // @ts-expect-error - TS2532 - Object is possibly 'undefined'.
    matrix[row][col] = value;
  }
  return matrix;
}

/**
 * Returns a Cell object with id and location after validating that the cell ID
 * has a valid entry in the locations object.
 */
export function makeCell(
  cellId: CellId,
  locations: LayoutLocations
): Cell | null | undefined {
  const location = locations[cellId];

  // For some reason, the cell couldn't be located
  if (!location) {
    reportBug(
      'LayoutError',
      'Cell could not be located in the layout children locations',
      {
        team: 'structure',
        ticket: 'https://webflow.atlassian.net/browse/ACT-1627',
      }
    );
    return null;
  }
  return {id: cellId, location};
}

/**
 * Given a grid item location, returns an array of coordinates that indicate the
 * position of each cell in the layout matrix.
 *
 * Example:
 * {gridRowStart: 1, gridRowEnd: 2, gridColumnStart: 2, gridColumnEnd: 4}
 *   =>[[0, 1], [0, 2]]
 */
export function makeCoordinatesFromLocation(
  location: CellLocation
): CellCoordinates {
  const {gridRowStart, gridRowEnd, gridColumnStart, gridColumnEnd} = location;
  const coords: Array<[RowCoord, ColumnCoord]> = [];

  for (let row = gridRowStart; row < gridRowEnd; row++) {
    for (let col = gridColumnStart; col < gridColumnEnd; col++) {
      coords.push([row - 1, col - 1]);
    }
  }
  return coords;
}

/**
 * Given a grid item location, returns an array of column coordinates that
 * indicate the column position of each cell in the layout matrix.
 *
 * Example:
 * {gridRowStart: 1, gridRowEnd: 2, gridColumnStart: 2, gridColumnEnd: 4}
 *   => [1, 2]
 */
export function makeColumnCoordinatesFromLocation(
  location: CellLocation
): ColumnCoord[] {
  const {gridColumnStart, gridColumnEnd} = location;
  const coords: Array<ColumnCoord> = [];

  for (let col = gridColumnStart; col < gridColumnEnd; col++) {
    coords.push(col - 1);
  }
  return coords;
}

/**
 * Given a grid item location, returns an array of row coordinates that
 * indicate the row position of each cell in the layout matrix.
 *
 * Example:
 * {gridRowStart: 1, gridRowEnd: 2, gridColumnStart: 2, gridColumnEnd: 4}
 *   => [0]
 */
export function makeRowCoordinatesFromLocation(
  location: CellLocation
): RowCoord[] {
  const {gridRowStart, gridRowEnd} = location;
  const coords: Array<RowCoord> = [];

  for (let row = gridRowStart; row < gridRowEnd; row++) {
    coords.push(row - 1);
  }
  return coords;
}

/**
 * Given a Cell, builds a list of adjacent coordinates in a given direction
 */
export function makeAdjacentCoordinates(
  cell: Cell,
  direction: CellDirection
): CellCoordinates {
  switch (direction) {
    case CELL_DIRECTIONS.TOP:
      return makeCoordinatesFromLocation({
        gridRowStart: cell.location.gridRowStart - 1,
        gridRowEnd: cell.location.gridRowStart,
        gridColumnStart: cell.location.gridColumnStart,
        gridColumnEnd: cell.location.gridColumnEnd,
      });

    case CELL_DIRECTIONS.BOTTOM:
      return makeCoordinatesFromLocation({
        gridRowStart: cell.location.gridRowEnd,
        gridRowEnd: cell.location.gridRowEnd + 1,
        gridColumnStart: cell.location.gridColumnStart,
        gridColumnEnd: cell.location.gridColumnEnd,
      });

    case CELL_DIRECTIONS.LEFT:
      return makeCoordinatesFromLocation({
        gridRowStart: cell.location.gridRowStart,
        gridRowEnd: cell.location.gridRowEnd,
        gridColumnStart: cell.location.gridColumnStart - 1,
        gridColumnEnd: cell.location.gridColumnStart,
      });

    case CELL_DIRECTIONS.RIGHT:
      return makeCoordinatesFromLocation({
        gridRowStart: cell.location.gridRowStart,
        gridRowEnd: cell.location.gridRowEnd,
        gridColumnStart: cell.location.gridColumnEnd,
        gridColumnEnd: cell.location.gridColumnEnd + 1,
      });
    default:
      return [];
  }
}

/**
 * Given a layout matrix and a set of coordinates, returns a list of cells
 */
export function lookupCellsByCoordinates(
  coordinates: CellCoordinates,
  matrix: LayoutMatrix,
  locations: LayoutLocations
): Array<Cell> {
  // Using a set to ensure uniqueness. Should be more performant than _.uniq
  const values = new Set();
  const cells: Array<Cell> = [];

  for (const [row, col] of coordinates) {
    const cellId = matrix[row]?.[col];
    if (!cellId) {
      continue;
    }

    if (values.has(cellId)) {
      continue;
    }
    values.add(cellId);

    const cell = makeCell(cellId, locations);
    if (cell) {
      cells.push(cell);
    }
  }
  return cells;
}

/**
 * Given a cell, it gets the adjacent Cells in a given direction
 */
export function getAdjacentCells(
  cell: Cell | null | undefined,
  direction: CellDirection,
  matrix: LayoutMatrix,
  locations: LayoutLocations
): Array<Cell> {
  if (!cell) {
    return [];
  }
  const adjacentCoordinates = makeAdjacentCoordinates(cell, direction);
  return lookupCellsByCoordinates(adjacentCoordinates, matrix, locations);
}

/**
 * Given a list of Cells and a subset of location keys, evaluates if all the Cells
 * have equal locations
 */
export function areAllCellsSameLocation(
  cells: Array<Cell>,
  locationKeys: Array<keyof CellLocation>
): boolean {
  const firstCell = cells[0];
  if (!firstCell) {
    return true;
  }

  for (const cell of cells) {
    for (const key of locationKeys) {
      if (cell.location[key] !== firstCell.location[key]) {
        return false;
      }
    }
  }
  return true;
}

/**
 * Given a set of coordinates, computes the row and col spans.
 */
export function computeSpansByCoordinates(
  coordinates: CellCoordinates
): CellSpans {
  let minRow = Infinity;
  let minCol = Infinity;
  let maxRow = -Infinity;
  let maxCol = -Infinity;
  for (const [row, col] of coordinates) {
    minRow = Math.min(minRow, row);
    maxRow = Math.max(maxRow, row);
    minCol = Math.min(minCol, col);
    maxCol = Math.max(maxCol, col);
  }

  return {
    rowSpan: maxRow - minRow + 1,
    colSpan: maxCol - minCol + 1,
  };
}

/**
 * Computes cell css styles.
 * If the cell spans only one row or column, we don't need to set the span because
 * it's the default Grid value.
 */
export function computeCellStyles({
  spans,
}: {
  spans: CellSpans;
}): WebflowCssProperties {
  return {
    gridRowStart: spans.rowSpan === 1 ? null : `span ${spans.rowSpan}`,
    gridRowEnd: spans.rowSpan === 1 ? null : `span ${spans.rowSpan}`,
    gridColumnStart: spans.colSpan === 1 ? null : `span ${spans.colSpan}`,
    gridColumnEnd: spans.colSpan === 1 ? null : `span ${spans.colSpan}`,
  };
}

/**
 * Helper function to deep copy the matrix
 */
export function deepCopyMatrix(matrix: LayoutMatrix): LayoutMatrix {
  return matrix.map((row) => [...row]);
}

/**
 * Helper method to split grid template value by each item
 * Example: 'minmax(75px, auto) 1fr' => ['minmax(75px, auto)', '1fr']
 */
export function getTemplateList(gridTemplateString: string | null): string[] {
  return splitCssValue(gridTemplateString, ' ');
}

/**
 * Helper method to get the safe length of a row in a layout matrix (i.e. the smallest row length)
 */
export function getSafeRowLength(matrix: LayoutMatrix, min = 0): number {
  return Math.min(...matrix.map((row) => row.length)) || min;
}
